import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import { usePopperTooltip } from 'react-popper-tooltip';
import { extendTailwindMerge } from 'tailwind-merge';
import { LocaleContext, isRtl } from '../../../i18n/src';
import { getDirectionAwarePlacement } from './utils/get_direction_aware_placement';
import { getPrimaryPlacement } from './utils/get_primary_placement';
import { getSecondaryPlacement } from './utils/get_secondary_placement';
const twMerge = extendTailwindMerge({
    prefix: 'nucleus-',
});
const defaultPlacement = 'bottom';
/**
 * A tooltip is a popup that displays information related to an element when the element receives keyboard focus, the mouse hovers over it or a touch device user taps on it.
 * A keyboard user can also press the 'Escape' key to remove element focus.
 *
 * @remarks
 *
 * Design guidelines dictate that tooltips should be used sparingly and that text should be kept short to at most 3 lines.
 *
 * @privateRemarks
 *
 * This component is based on the `react-popper-tooltip` library which uses `popper.js` under the hood but with some nice utilities on top compared to e.g. `react-popper`.
 * The libraries do not support RTL mode out of the box, so we have to do some extra work to flip the placement of the tooltip.
 * And since tailwind-vanilla-rtl translates e.g. mr and ml we need to do an extra `isLtr ? '-ml-1' : '-mr-1'` check in some places.
 */
export const Tooltip = ({ title, placement = defaultPlacement, children, as: WrapperElement = 'button', className, wrapperClassName, delayShow = 0, delayHide = 0, displayOnlyOnOverflow = false, }) => {
    var _a, _b;
    const locale = useContext(LocaleContext);
    const isLtr = !isRtl(locale);
    const directionAwarePlacement = getDirectionAwarePlacement({
        placement,
        isLtr,
    });
    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, state, visible, triggerRef } = usePopperTooltip({
        placement: directionAwarePlacement,
        trigger: ['hover', 'focus'],
        delayShow,
        delayHide,
    }, {
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 6], // the second value is the distance between the tooltip and the reference element
                },
            },
            {
                name: 'arrow',
                options: {
                    // https://popper.js.org/docs/v2/modifiers/arrow/#padding
                    padding: useCallback(({ popper }) => {
                        // We want to set a padding of 16px between the arrow and the edge of the popper, but only if the popper has enough width or height.
                        // We dont want the arrow to be offset more than half the width or height in the case of very small poppers, so we choose the minimum
                        // between half the width/height (minus 8px accounting for the arrow) and our desired 16px padding.
                        const paddingX = Math.min(popper.width / 2 - 8, 16);
                        const paddingY = Math.min(popper.height / 2 - 8, 16);
                        return {
                            left: paddingX,
                            right: paddingX,
                            top: paddingY,
                            bottom: paddingY,
                        };
                    }, []),
                },
            },
        ],
    });
    const tooltipId = `tooltip-${`${title}`.toLowerCase().replace(/\s+/g, '-')}`;
    const primaryPlacement = (_a = getPrimaryPlacement(state === null || state === void 0 ? void 0 : state.placement)) !== null && _a !== void 0 ? _a : defaultPlacement;
    const secondaryPlacement = (_b = getSecondaryPlacement(state === null || state === void 0 ? void 0 : state.placement)) !== null && _b !== void 0 ? _b : 'center';
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.currentTarget.blur();
        }
    };
    const isOverflowing = triggerRef && triggerRef.scrollWidth > triggerRef.clientWidth;
    return (_jsxs(_Fragment, { children: [_jsx(WrapperElement, { ref: setTriggerRef, onKeyDown: handleKeyDown, className: twMerge('nucleus-box-border nucleus-inline-flex nucleus-cursor-pointer nucleus-bg-transparent nucleus-font-sans nucleus-text-base nucleus-normal-case nucleus-m-0 nucleus-border-0 focus-visible:nucleus-outline-2 focus-visible:nucleus-outline-offset-4 focus-visible:nucleus-outline-primary-500', wrapperClassName), "aria-describedby": visible ? tooltipId : undefined, children: children }), visible &&
                (!displayOnlyOnOverflow || isOverflowing) &&
                createPortal(_jsxs("div", Object.assign({ role: 'tooltip', id: tooltipId, ref: setTooltipRef }, getTooltipProps(), { className: `nucleus-absolute nucleus-pointer-events-none nucleus-z-50 nucleus-max-w-tooltip nucleus-animate-fadeIn nucleus-bg-neutral-700 nucleus-font-sans nucleus-text-xs nucleus-font-medium nucleus-text-white nucleus-shadow-lg nucleus-rounded-lg nucleus-p-3 ${
                    // Due to the arrow padding we need to negatively offset the tooltip in non-center secondary placements so the arrow still points to the reference element.
                    {
                        top: '-nucleus-mt-4',
                        bottom: '-nucleus-mb-4',
                        // See note in @privateRemarks about the isLtr check.
                        left: isLtr ? '-nucleus-ml-4' : '-nucleus-mr-4',
                        right: isLtr ? '-nucleus-mr-4' : '-nucleus-ml-4',
                        center: '',
                    }[secondaryPlacement]}
            ${className}
           `, children: [_jsx("div", Object.assign({}, getArrowProps(), { 
                            // The clip path is a plain rhombus
                            className: `nucleus-aspect-square nucleus-w-4 nucleus-bg-neutral-700 [clip-path:polygon(50%_0%,100%_50%,50%_100%,0%_50%)]
            ${{
                                top: '-nucleus-bottom-2',
                                bottom: '-nucleus-top-2',
                                // See note in @privateRemarks about the isLtr check.
                                right: isLtr ? '-nucleus-left-2' : '-nucleus-right-2',
                                left: isLtr ? '-nucleus-right-2' : '-nucleus-left-2',
                            }[primaryPlacement]}` })), title] })), document.body)] }));
};
//# sourceMappingURL=tooltip.js.map